import React from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Link,
    IconButton,
    Divider,
    Stack,
    useTheme
} from '@mui/material';
import {
    Facebook,
    Twitter,
    Instagram,
    LinkedIn,
    YouTube,
    Email,
    Phone,
    LocationOn
} from '@mui/icons-material';
import { getOrDefault } from '../../../../Helpers/Utils';
import ApplicationLogo from '../../../../Components/ApplicationLogo';
import { ComprehensiveFooterProps, FooterLink, LinkSectionProps, NavigationSection, SocialIconProps } from './types';
import { useMicrospecDynamicUIContext } from '../../../../GlobalProvider';
import { useMicrospecThemeLanguageContext } from '../../../../Themes';

const ComprehensiveFooter: React.FC<ComprehensiveFooterProps> = ({ spec, type, index }) => {
    const { config } = useMicrospecDynamicUIContext();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const createdWithLove = localizationProvider.translateByType({
        type: "Internal",
        text: 'createdWithLove'
    });
    const applicationName = localizationProvider.translateByType({
        type: "Localized",
        text: "application.name"
    });
    const applicationSlogan = localizationProvider.translateByType({
        type: "Localized",
        text: "application.slogan"
    });
    const theme = useTheme();

    // Extract spec properties with defaults
    const backgroundColor = getOrDefault(spec.backgroundColor, theme.palette.background.paper);
    const textColor = getOrDefault(spec.textColor, theme.palette.text.primary);
    const accentColor = getOrDefault(spec.accentColor, theme.palette.mode === 'dark' ? "#fff" : theme.palette.primary.main);
    const companyName = getOrDefault(spec.companyName, getOrDefault(applicationName, 'Your Company'));
    const copyrightsReserved = localizationProvider.translateByType({
        type: "Internal",
        text: 'copyrightsReserved'
    });
    const companyDescription = getOrDefault(spec.companyDescription, getOrDefault(applicationSlogan, ""));
    const copyright = getOrDefault(spec.copyright, `${companyName}. ${copyrightsReserved} © ${new Date().getFullYear()} `);

    const navigationLinks = getOrDefault(spec.navigationLinks, []);

    // Contact information
    const contactInfo = getOrDefault(spec.contactInfo, {
        email: config?.application?.information?.contactEmail,
        phone: config?.application?.information?.contactPhone,
        address: config?.application?.information?.address
    });

    // Social media links
    const socialMedia = getOrDefault(spec.socialMedia, {
        facebook: config?.application?.information?.socialMedia?.facebook,
        twitter: config?.application?.information?.socialMedia?.twitter,
        instagram: config?.application?.information?.socialMedia?.instagram,
        linkedin: config?.application?.information?.socialMedia?.linkedin,
        youtube: config?.application?.information?.socialMedia?.youtube
    });

    const LinkSection: React.FC<LinkSectionProps> = ({ title, links }) => (
        <Box>
            <Typography
                variant="h6"
                sx={{
                    color: textColor,
                    fontWeight: 600,
                    mb: 2,
                    fontSize: '1.1rem'
                }}
            >
                {title}
            </Typography>
            <Stack gap={1}>
                {links.map((link: FooterLink, idx: number) => (
                    <Link
                        key={idx}
                        href={link.href}
                        sx={{
                            color: textColor,
                            textDecoration: 'none',
                            fontSize: '0.9rem',
                            opacity: 0.8,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                opacity: 1,
                                color: accentColor,
                                transform: 'translateX(4px)'
                            }
                        }}
                    >
                        {link.label}
                    </Link>
                ))}
            </Stack>
        </Box>
    );

    const SocialIcon: React.FC<SocialIconProps> = ({ icon: Icon, href }) => (
        <IconButton
            href={href}
            sx={{
                color: textColor,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                    backgroundColor: accentColor,
                    color: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${accentColor}40`
                }
            }}
        >
            <Icon />
        </IconButton>
    );

    return (
        <Box
            id={spec.id}
            component="footer"
            sx={{
                backgroundColor,
                color: textColor,
                py: 6,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '1px',
                    background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`
                },
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Company Info */}
                    <Grid item xs={12} md={4}>
                        <Box>
                            <ApplicationLogo
                                href={undefined}
                                enableHoveringEffect={false}
                                animate={false}
                                overrideDefaultLogo={false}
                                additionalTextLines={[
                                    companyName,
                                    companyDescription
                                ]}
                            />

                            {/* Contact Info */}
                            <Stack gap={1.5} sx={{ mb: 3 }}>
                                {contactInfo.email && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Email sx={{ color: accentColor, fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ color: textColor, opacity: 0.8 }}>
                                        {contactInfo.email}
                                    </Typography>
                                </Box>}
                                {contactInfo.phone && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Phone sx={{ color: accentColor, fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ color: textColor, opacity: 0.8 }}>
                                        {contactInfo.phone}
                                    </Typography>
                                </Box>}
                                {contactInfo.address && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationOn sx={{ color: accentColor, fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ color: textColor, opacity: 0.8 }}>
                                        {contactInfo.address}
                                    </Typography>
                                </Box>}
                            </Stack>

                            {/* Social Media */}
                            <Stack direction="row" gap={1}>
                                {socialMedia.facebook && <SocialIcon icon={Facebook} href={socialMedia.facebook} />}
                                {socialMedia.twitter && <SocialIcon icon={Twitter} href={socialMedia.twitter} />}
                                {socialMedia.instagram && <SocialIcon icon={Instagram} href={socialMedia.instagram} />}
                                {socialMedia.linkedin && <SocialIcon icon={LinkedIn} href={socialMedia.linkedin} />}
                                {socialMedia.youtube && <SocialIcon icon={YouTube} href={socialMedia.youtube} />}
                            </Stack>
                        </Box>
                    </Grid>

                    {/* Navigation Links */}
                    {
                        navigationLinks.map((section: NavigationSection, idx: number) => (
                            <Grid item xs={12} sm={6} md={2} key={idx}>
                                <LinkSection title={section.title} links={section.links} />
                            </Grid>
                        ))
                    }
                </Grid>

                {/* Divider */}
                <Divider
                    sx={{
                        my: 4,
                        borderColor: `${textColor}20`,
                        background: `linear-gradient(90deg, transparent, ${textColor}40, transparent)`
                    }}
                />

                {/* Copyright */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: textColor,
                            opacity: 0.7
                        }}
                    >
                        {copyright}
                    </Typography>
                    <Typography
                        component={"a"}
                        href="https://microspec.io"
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="body2"
                        sx={{
                            color: textColor,
                            opacity: 0.7
                        }}
                    >
                        {createdWithLove}
                    </Typography>
                </Box>
            </Container>

            {/* Decorative Background Elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '200px',
                    height: '200px',
                    background: `radial-gradient(circle, ${accentColor}10, transparent)`,
                    borderRadius: '50%',
                    transform: 'translate(50%, -50%)',
                    pointerEvents: 'none'
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '150px',
                    height: '150px',
                    background: `radial-gradient(circle, ${accentColor}08, transparent)`,
                    borderRadius: '50%',
                    transform: 'translate(-50%, 50%)',
                    pointerEvents: 'none'
                }}
            />
        </Box>
    );
};

export default ComprehensiveFooter;
